import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMetronomeStore } from '../../../pages/View/components/plugins/MetronomePlugin/MetronomePlugin';
import { PICKER_SOUNDS, auditionSound } from '../../../utils/metronomeClick';
import { getSetting, setSetting, settingKeys } from '../../../utils/settings';
import RangeSlider from '../../common/RangeSlider';
import ToggleSwitch from '../../common/ToggleSwitch';
import SettingsModal from '../../common/SettingsModal';
import ModalButton from '../../common/ModalButton';
import NoSoundHelp from '../../common/NoSoundHelp';

import { ReactComponent as SoundHighIcon } from '../../../icons/sound-high-solid.svg';
import { ReactComponent as SoundOffIcon } from '../../../icons/sound-off-solid.svg';
import './MetronomeVolumeModal.scss';

// Each label is under dialogs.metronome.subdivisions
const SUBDIVISION_OPTIONS = [
  { value: 1, label: 'off' },
  { value: 2, label: 'eighths' },
  { value: 3, label: 'triplets' },
  { value: 4, label: 'sixteenths' },
];

const MetronomeVolumeModal = ({ isOpen, onClose, volume, soundType, onVolumeChange, onSoundTypeChange }) => {
  const { t } = useTranslation();
  const setAccentBeat = useMetronomeStore((s) => s.setAccentBeat);
  const subdivision = useMetronomeStore((s) => s.subdivision);
  const setSubdivision = useMetronomeStore((s) => s.setSubdivision);
  const [localVolume, setLocalVolume] = useState(volume);
  const [localSoundType, setLocalSoundType] = useState(soundType);
  const [continuousMetronome, setContinuousMetronome] = useState(() => {
    const stored = getSetting(settingKeys.metronomeContinuous);
    return stored !== undefined ? stored : false; // Default to false (OFF)
  });
  const [followAutoScroll, setFollowAutoScroll] = useState(() => {
    const stored = getSetting(settingKeys.metronomeFollowAutoScroll);
    return stored !== undefined ? stored : false; // Default to false (OFF)
  });
  const [accentBeat, setAccentBeatState] = useState(() => {
    const stored = getSetting(settingKeys.metronomeAccentBeat);
    return stored !== undefined ? stored : true; // Default to true (ON)
  });

  useEffect(() => {
    setLocalVolume(volume);
  }, [volume]);

  useEffect(() => {
    setLocalSoundType(soundType);
  }, [soundType]);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setLocalVolume(newVolume);
    onVolumeChange(newVolume);
  };

  const handleSoundTypeChange = (e) => {
    const newSoundType = e.target.value;
    setLocalSoundType(newSoundType);
    onSoundTypeChange(newSoundType);

    // A running metronome already plays the new sound on its next beat
    if (!useMetronomeStore.getState().activeMetronomeId) auditionSound(newSoundType, localVolume);
  };

  const handleContinuousToggle = () => {
    const newValue = !continuousMetronome;
    setContinuousMetronome(newValue);
    setSetting(settingKeys.metronomeContinuous, newValue);
  };

  const handleFollowAutoScrollToggle = () => {
    const newValue = !followAutoScroll;
    setFollowAutoScroll(newValue);
    setSetting(settingKeys.metronomeFollowAutoScroll, newValue);
  };

  const handleAccentBeatToggle = () => {
    const newValue = !accentBeat;
    setAccentBeatState(newValue);
    setAccentBeat(newValue);
  };

  return (
    <SettingsModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialogs.metronome.title')}
      footer={<ModalButton onClick={onClose}>{t('common.done')}</ModalButton>}
    >
      <NoSoundHelp hasVolumeSliders />
      <div className="sound-type-control">
        <label htmlFor="sound-type-select">{t('dialogs.metronome.soundType')}</label>
        <select
          id="sound-type-select"
          value={localSoundType}
          onChange={handleSoundTypeChange}
          className="sound-type-select mg-custom-select"
        >
          {PICKER_SOUNDS.map((sound) => (
            <option key={sound.value} value={sound.value}>
              {t(`dialogs.metronome.sounds.${sound.value}`, { defaultValue: sound.label })}
            </option>
          ))}
        </select>
      </div>
      <div className="sound-type-control">
        <label htmlFor="subdivision-select">{t('dialogs.metronome.subdivision')}</label>
        <select
          id="subdivision-select"
          value={subdivision}
          onChange={(e) => setSubdivision(Number(e.target.value))}
          className="sound-type-select mg-custom-select"
        >
          {SUBDIVISION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(`dialogs.metronome.subdivisions.${option.label}`)}
            </option>
          ))}
        </select>
      </div>
      <RangeSlider
        id="volume-slider"
        // The bar reads as silent at zero, so the icon riding on it has to say so too
        label={localVolume > 0 ? <SoundHighIcon aria-hidden="true" /> : <SoundOffIcon aria-hidden="true" />}
        ariaLabel={t('player.volume')}
        value={localVolume}
        onChange={handleVolumeChange}
      />
      <ToggleSwitch
        name="continuous-metronome"
        label={t('dialogs.metronome.continuous')}
        checked={continuousMetronome}
        onChange={handleContinuousToggle}
        description={t('dialogs.metronome.continuousHint')}
        showOnOffText={true}
      />
      <ToggleSwitch
        name="follow-auto-scroll"
        label={t('dialogs.metronome.followAutoScroll')}
        checked={followAutoScroll}
        onChange={handleFollowAutoScrollToggle}
        description={t('dialogs.metronome.followAutoScrollHint')}
        showOnOffText={true}
      />
      <ToggleSwitch
        name="accent-beat"
        label={t('dialogs.metronome.accent')}
        checked={accentBeat}
        onChange={handleAccentBeatToggle}
        description={t('dialogs.metronome.accentHint')}
        showOnOffText={true}
      />
    </SettingsModal>
  );
};

export default MetronomeVolumeModal;
